/**
 * Lock Store
 * ---------------------------------------------
 * Reactive store for managing optimistic resource locks.
 *
 * PURPOSE
 *  - Acquire, refresh, and release locks on resources.
 *  - Track lock state for resources being edited.
 *  - Automatically refresh locks before expiry.
 *  - Receive SSE events for lock changes (including takeovers by other users).
 *
 * SSE TOPICS
 *  - `locks/{resourceType}/{resourceId}`: one resource's lock events, heard by whoever may read the
 *    record (#398 C10). There is no whole-type topic: the server refuses `locks/{resourceType}` to
 *    everyone, since no read answers every record of a type.
 *
 * USAGE
 *  ```ts
 *  // The resource name is the SAME string the server's jobs lock under - build it with the
 *  // module's helper (Glass: certificateLockResource(type) -> 'Certificate-AS2208'; Product
 *  // Registry: 'Report-AS2047'), never a bare word a server job would not recognise.
 *  const resource = certificateLockResource('AS2208');
 *  const lock = await lockStore.acquireLock(resource, certId);
 *  if (lock.success) {
 *    // Have the lock, can edit
 *    // Lock will auto-refresh; listen for events
 *    lockStore.subscribe(resource, certId, (state) => {
 *      if (state.takenOver) showTakenOver(state.holder?.userId); // named by id (D-162)
 *    });
 *  } else {
 *    // Locked by someone else
 *    showHeldBy(lock.currentHolder?.userId); // named by id (D-162)
 *  }
 *  // When done editing:
 *  await lockStore.releaseLock(resource, certId);
 *  ```
 */

import {
  getSseHub,
  type HubEvent,
  type TopicComparableEventBase,
  type LockEventType,
} from '../notifications/sse-hub';
import {
  getLockingApi,
  type LockResult,
  type ResourceLockDto,
  type LockHolderDto,
} from '../../api/locking/locking-api';
import { defaultDomain } from '../../api/default-domain';
import { ApiFactory } from '../../api/agwasuite-api-factory';
import { CSRF_HEADER_NAME } from '../../api/agwasuite-api';
import { clearTabSessionId, readTabSessionId } from '../session-storage-keys';
import {
  ensureTabSessionId,
  lastTabSessionIssueWentUnanswered,
} from '../../services/tab-session';
import { isUnanswered } from '../../api/unanswered-request';

// ─────────────────────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────────────────────

/** Lock state for a single resource */
export interface ResourceLockState {
  resourceType: string;
  resourceId: string;
  /** Whether we currently hold the lock */
  isOwner: boolean;
  /** Current lock holder info (may be us or another user) */
  holder?: LockHolderDto;
  /**
   * Whether the holder is THIS browser tab, whoever acquired it. `isOwner` is only set by locks
   * this store acquired itself; a lock a server job acquired on the tab's behalf (Start Review,
   * Take Over Review) is held by us without being owned by us in that sense, and a page that read
   * `holder && !isOwner` as "someone else has it" showed the auditor their own lock as another
   * person's. Those jobs no longer receive a session from the caller - the server uses the PROVED
   * tab session - so the lock they take belongs to this tab by construction (#366).
   */
  heldByThisSession: boolean;
  /**
   * True on the ONE emit that reports a release this tab itself caused: its own releaseLock call,
   * or a server job it asked for that released the lock as part of the decision (announced ahead
   * through `expectServerRelease`). Consumers judge loss as "was ours, now not ours, and NOT
   * releasedByThisTab": without this every Save, Submit, Approve and Cancel Review showed the
   * person a "released from another tab" banner for a release they had just clicked (@LOCK-004).
   * One signal here, rather than a flag in every consumer, because the consumers kept missing paths.
   */
  releasedByThisTab: boolean;
  /** Fencing token for the current lock */
  fencingToken?: number;
  /** When our lock expires (if we own it) */
  leaseExpiresAt?: Date;
  /** True if we lost the lock to another user */
  takenOver: boolean;
  /** Error message if lock operation failed */
  error?: string;
  /** Whether a lock operation is in progress */
  loading: boolean;
}

export type ResourceLockListener = (state: ResourceLockState) => void;

/** SSE lock event structure from server */
export interface LockEventPayload extends TopicComparableEventBase {
  eventName: LockEventType;
  topic: string;
  resourceType: string;
  resourceId: string;
  ownerUserId: string;
  ownerSessionId: string;
  fencingToken: number;
  leaseExpiresAt: string;
}

/** Tracked resource for auto-refresh */
interface TrackedLock {
  resourceType: string;
  resourceId: string;
  fencingToken: number;
  leaseExpiresAt: Date;
  refreshTimer?: ReturnType<typeof setTimeout>;
  listeners: Set<ResourceLockListener>;
  sseUnsubscribe?: () => void;
  state: ResourceLockState;
  /**
   * Set by `expectServerRelease` (a server job this tab asked for is about to release this lock)
   * and by `releaseLock` itself before it asks (@LOCK-025), so the next lock-released for our
   * session is ours, whether it lands before or after the request's answer. Bounded: if the event never arrives (the
   * job was refused and the caller forgot to clear, the stream was down) the expectation lapses
   * rather than swallowing the next real loss.
   */
  expectServerReleaseUntil?: number;
}

// ─────────────────────────────────────────────────────────────────────────────
// Constants
// ─────────────────────────────────────────────────────────────────────────────

/** Default lease duration in seconds (2 minutes) */
const DEFAULT_LEASE_SECONDS = 120;

/** Refresh lock when this much time remains (30 seconds before expiry) */
const REFRESH_BUFFER_MS = 30_000;

/** Minimum refresh interval to avoid hammering the server */
const MIN_REFRESH_INTERVAL_MS = 10_000;

/**
 * How long an announced server-side release stays expected. A decision job releases inside its own
 * transaction and the event follows within a second or two; thirty seconds is generous enough for a
 * slow stream and short enough that a stale expectation cannot hide a later real loss.
 */
const EXPECT_SERVER_RELEASE_MS = 30_000;

// ─────────────────────────────────────────────────────────────────────────────
// Store Implementation
// ─────────────────────────────────────────────────────────────────────────────

class LockStore {
  private readonly trackedLocks = new Map<string, TrackedLock>();

  /**
   * Guards against releasing twice when a browser fires both unload events. Not merely tidiness:
   * the second call would be a redundant request issued while the page is being torn down.
   */
  private unloadReleaseSent = false;

  constructor() {
    if (typeof window !== 'undefined') {
      // BOTH events, because no single one is reliable. `beforeunload` does not fire on mobile
      // Safari or when a backgrounded tab is discarded; `pagehide` does, and is the only one that
      // fires when the page goes into the back/forward cache. Registering one of them is how a
      // lock survives its tab (@LOCK-015).
      window.addEventListener('pagehide', this.handleUnload);
      window.addEventListener('beforeunload', this.handleUnload);
      // And the other half of pagehide: a page that went into the back/forward cache can come back.
      window.addEventListener('pageshow', this.handlePageShow);
    }
  }

  private handleUnload = (): void => {
    this.releaseSessionLocksOnUnload();
  };

  /**
   * A page restored from the back/forward cache has already released its locks and does not know it.
   *
   * <p><c>pagehide</c> fires when a page ENTERS the bfcache, not only when it is destroyed, so going
   * Back released everything this tab held. The page then comes back alive with its JavaScript state
   * exactly as it was, which is wrong in two ways that both show as the product lying to the person:
   * every tracked lock still says <c>isOwner</c> for a lock the server has already removed, so the
   * page shows Editing on a record it no longer holds; and <c>unloadReleaseSent</c> is still true, so
   * when this page is later genuinely closed it releases nothing and its locks sit until the lease
   * lapses - @LOCK-015 failing for the back-then-close case.</p>
   *
   * <p>The session itself is untouched and still provable, which is correct: releasing locks is not
   * ending a session. So the tab can simply take its locks again if the person carries on.</p>
   *
   * <p>Only when <c>persisted</c> is true. An ordinary load fires <c>pageshow</c> too, and there is
   * nothing to reconcile then - the store is empty.</p>
   */
  private handlePageShow = (event: PageTransitionEvent): void => {
    if (!event.persisted) return;

    // Re-arm: this page is live again and its eventual close must release whatever it re-takes.
    this.unloadReleaseSent = false;

    void this.reconcileLocksAfterRestore();
  };

  /**
   * Ask the server who holds each lock this page thinks it owns, and believe the answer.
   *
   * <p>Marked <c>takenOver</c> rather than silently cleared, because that is the state the pages
   * already handle: they drop edit mode and reload from canonical server state, which is exactly
   * what a restored page needs to do. Reusing it means no page needs to learn about the bfcache.</p>
   */
  private async reconcileLocksAfterRestore(): Promise<void> {
    const mySession = this.sessionId;

    for (const tracked of this.trackedLocks.values()) {
      // Either route to holding it counts: acquired here, or taken for this tab by a server job
      // (a review lock), which leaves isOwner false while the holder is our session.
      const wasOurs =
        tracked.state.isOwner || tracked.state.holder?.sessionId === mySession;
      if (!wasOurs) continue;

      const status = await this.fetchLockStatus(
        tracked.resourceType,
        tracked.resourceId
      );

      // Still ours - the page was restored fast enough that nothing had changed, or we re-took it.
      if (status && mySession && status.ownerSessionId === mySession) continue;

      tracked.state.isOwner = false;
      tracked.state.heldByThisSession = false;
      // A takeover only when somebody else holds it now; a lock that is simply gone is a loss with
      // nobody to name, and saying "taken over" with no holder would be a false statement.
      tracked.state.takenOver = !!status && status.ownerSessionId !== mySession;
      // Mapped, not assigned: the status DTO names the holder's fields owner*, the holder record
      // names them plainly, and the same mapping is done where a subscription fetches initial state.
      tracked.state.holder = status
        ? {
            userId: status.ownerUserId,
            sessionId: status.ownerSessionId,
            leaseExpiresAt: status.leaseExpiresAt,
            acquiredAt: status.acquiredAt ?? new Date().toISOString(),
          }
        : undefined;
      tracked.state.error = undefined;
      this.emit(tracked);
    }
  }

  /**
   * Releases everything this tab holds, in ONE request, as the page goes away.
   *
   * <p>WHY NOT `navigator.sendBeacon`, which this used to use: a beacon cannot set headers. The
   * server judges lock ownership by the tab session on `X-AGWA-Tab-Session` and authenticates with
   * a cookie, so a beacon could carry neither and every unload release was refused silently, the
   * lock lingering until its lease lapsed about two minutes later (@LOCK-015). `fetch` with
   * `keepalive` outlives the page exactly as a beacon does AND sends headers and credentials, so
   * it is strictly better here; the beacon's only advantage was never one we could use.</p>
   *
   * <p>ONE request, not one per lock. The old loop asked the server to release each lock the CLIENT
   * believed it held, which misses any it has forgotten and needs N requests at the moment the
   * browser is least willing to make them. `release-session` releases by session, server-side, so
   * the client does not need to know what it was holding.</p>
   *
   * <p>Fire-and-forget by necessity: the page is going. The promise is deliberately not awaited and
   * its rejection deliberately swallowed - there is nothing left to report to, and an unhandled
   * rejection during unload is noise in the console of a page that no longer exists.</p>
   */
  private releaseSessionLocksOnUnload(): void {
    if (this.unloadReleaseSent) return;

    const tabSessionId = this.sessionId;
    // Nothing to release: a tab never issued a session cannot own a lock.
    if (!tabSessionId) return;

    this.unloadReleaseSent = true;
    const baseUrl = defaultDomain ?? window.location.origin;

    void fetch(`${baseUrl}/api/locks/release-session`, {
      method: 'POST',
      keepalive: true,
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        'X-AGWA-Tab-Session': tabSessionId,
        // The ACCOUNT key too, and it is not optional: it selects WHICH auth cookie the server
        // reads. Without it the request is unauthenticated and the release is refused, which is
        // the same silent failure in a new disguise.
        ...(ApiFactory.getAccountKey()
          ? { 'X-Session-Id': ApiFactory.getAccountKey() as string }
          : {}),
        // And the CSRF header, because this is a POST and the server demands it on every
        // state-changing request. Until Unit E the two lock-release paths were exempt, an
        // exemption written for sendBeacon, which could not set headers; fetch can, so the
        // exemption is gone and this request is held to the same rule as every other one.
        // Without it the release is refused "CSRF protection failed: Missing required header",
        // silently, the lock lingering until its lease lapses (@LOCK-015).
        ...(ApiFactory.getUserId()
          ? { [CSRF_HEADER_NAME]: ApiFactory.getUserId() as string }
          : {}),
      },
      body: '{}',
    }).catch(() => {
      // See the note above: the page is unloading and there is nobody to tell.
    });
  }

  /** Get a fresh API instance with current auth/CSRF tokens */
  private get api() {
    return getLockingApi();
  }

  /** Build a composite key for the lock map */
  private lockKey(resourceType: string, resourceId: string): string {
    return `${resourceType.toLowerCase()}:${resourceId.toLowerCase()}`;
  }

  /**
   * Whether the server refused this request because it could not prove the caller's session.
   *
   * <p>Matched on the status AND the message, because the api surface carries no error code for it.
   * The text is the server's, in <c>LockingJobs</c>; if it changes there this stops matching and the
   * tab loses its self-heal, so the two are kept deliberately close in wording and the match is
   * loose enough to survive punctuation.</p>
   */
  private isUnprovedSessionRefusal(response: {
    success: boolean;
    status?: number;
    error?: string;
  }): boolean {
    return (
      !response.success &&
      response.status === 400 &&
      /no active session/i.test(response.error ?? '')
    );
  }

  /**
   * This tab's server-issued session, or empty before one has been issued.
   *
   * Read from storage rather than from `SSEClient`: the stream is a consumer of this value, not its
   * owner, so lock ownership no longer depends on the stream having connected first (#366).
   */
  private get sessionId(): string {
    return readTabSessionId() ?? '';
  }

  // ─────────────────────────────────────────────────────────────────────────────
  // Public API
  // ─────────────────────────────────────────────────────────────────────────────

  /**
   * Acquire a lock on a resource. Returns the result of the lock attempt.
   * If successful, the lock will be auto-refreshed until released.
   */
  async acquireLock(
    resourceType: string,
    resourceId: string,
    leaseDurationSeconds = DEFAULT_LEASE_SECONDS
  ): Promise<LockResult> {
    const key = this.lockKey(resourceType, resourceId);
    let tracked = this.trackedLocks.get(key);

    // Initialize tracked lock if not exists
    if (!tracked) {
      tracked = this.createTrackedLock(resourceType, resourceId);
      this.trackedLocks.set(key, tracked);
    }

    // Mark as loading
    tracked.state.loading = true;
    this.emit(tracked);

    try {
      // ENSURE A SESSION AT THE POINT OF NEED (@LOCK-016). Boot issuance is the fast path and
      // normally this is already stored, so this costs a storage read. But if issuance failed at
      // load - the endpoint blipped while the event stream connected fine - nothing else would ever
      // ask again, and the tab would hold no locks until the person reloaded. Taking a lock is
      // precisely the moment a session becomes necessary, so it is the honest moment to obtain one.
      //
      // Without this the failure is SILENT and indistinguishable from someone else holding the
      // lock: the acquire goes out with no session, the server refuses it, and Lock for Edit simply
      // does not take. That is the refusal-must-be-honest rule in D-115.
      const tabSessionId = await ensureTabSessionId();
      if (!tabSessionId && lastTabSessionIssueWentUnanswered()) {
        // The server never answered the session request: the connection monitor presents that,
        // so the lock's state stays exactly as it was and the page is told only that it went
        // unanswered (D-144).
        return this.unansweredAcquire(tracked);
      }
      if (!tabSessionId) {
        // Still none: the server is unreachable or refused to issue one. Say so, rather than
        // sending an acquire that is certain to be refused for a reason the person cannot read.
        tracked.state.loading = false;
        tracked.state.error =
          'Could not start an editing session. Check your connection and try again.';
        this.emit(tracked);
        return {
          success: false,
          errorMessage:
            'Could not start an editing session. Check your connection and try again.',
        };
      }

      const input = { resourceType, resourceId, leaseDurationSeconds };
      let response = await this.api.acquire(input);

      // SELF-HEAL A SESSION THE SERVER CAN NO LONGER PROVE.
      //
      // Holding a stored id is not the same as holding a session the server will accept. The row
      // can be gone underneath us: ended on another device by a sign-out, removed by the retention
      // purge, or lost to a database restore. The tab cannot tell - it still has an id - so without
      // this it would go on quoting a dead session, hold no locks, and say nothing. That is the
      // same silent state @LOCK-016 removed, arriving by a different route, and D-115 says a
      // refusal has to be honest.
      //
      // Discard, ask for a new one, and try once more. ONCE: a second identical refusal is not a
      // stale session but something we cannot fix by asking again, and retrying past that would
      // turn an honest message into a spin.
      if (this.isUnprovedSessionRefusal(response)) {
        clearTabSessionId();
        const reissued = await ensureTabSessionId();
        if (reissued) {
          response = await this.api.acquire(input);
        }
      }

      // A 409 IS THE LOCK MOVING WHILE WE ASKED, NOT THE RECORD (D-164, #398 C15 f). The lock row
      // carries no concurrency token, so the acquire's save can only lose to a change that removed
      // the row it read - a release, an expiry purge or a sign-out landing between its read and its
      // write - and the job answers that 409 with the record-changed sentence, which would tell the
      // person to refresh a page whose record nobody touched. Asked again, the acquire reads the lock
      // as it now stands and gives the honest answer: taken, or held by whoever took it. Once, as
      // the session self-heal above: a second 409 is reported as it came.
      if (!response.success && response.status === 409) {
        response = await this.api.acquire(input);
      }

      if (isUnanswered(response)) {
        return this.unansweredAcquire(tracked);
      }

      if (!response.success || !response.data) {
        // A refusal that survived the re-issue, or the server being unreachable. Both are honest
        // failures to start editing rather than "somebody else has it", and are said as such.
        const unproved = this.isUnprovedSessionRefusal(response);
        const message = unproved
          ? 'Could not start an editing session. Check your connection and try again.'
          : (response.error ?? 'Could not acquire the edit lock.');
        tracked.state.loading = false;
        tracked.state.error = message;
        this.emit(tracked);
        // The server's own refusal also goes back as it was sent, for the error toast to resolve
        // once (@VOC-SERVER-002); the store's own sentence has no kept form.
        const errorKept = unproved ? undefined : response.errorKept;
        return {
          success: false,
          errorMessage: message,
          ...(errorKept ? { errorKept } : {}),
        };
      }

      const result = response.data;
      tracked.state.loading = false;

      if (result.success) {
        // We got the lock
        tracked.fencingToken = result.fencingToken!;
        tracked.leaseExpiresAt = new Date(result.leaseExpiresAt!);
        tracked.state.isOwner = true;
        tracked.state.fencingToken = result.fencingToken;
        tracked.state.leaseExpiresAt = tracked.leaseExpiresAt;
        tracked.state.holder = {
          userId: '', // We don't have our own userId here
          // Our own session: we just acquired it, so this tab IS the holder. Populated so that
          // `holder.sessionId === this.sessionId` comparisons elsewhere hold immediately, rather
          // than only after the server's lock-acquired event arrives and replaces this record.
          sessionId: this.sessionId,
          leaseExpiresAt: result.leaseExpiresAt!,
          acquiredAt: new Date().toISOString(),
        };
        tracked.state.takenOver = false;
        tracked.state.error = undefined;

        // Start auto-refresh and SSE subscription
        this.scheduleRefresh(tracked);
        this.subscribeToLockEvents(tracked);
      } else {
        // Someone else has the lock
        tracked.state.isOwner = false;
        tracked.state.holder = result.currentHolder;
        tracked.state.error = result.errorMessage;
      }

      this.emit(tracked);
      return result;
    } catch (err) {
      tracked.state.loading = false;
      tracked.state.error =
        err instanceof Error ? err.message : 'Unknown error';
      this.emit(tracked);
      return { success: false, errorMessage: tracked.state.error };
    }
  }

  /**
   * An acquire whose request got no answer (D-144): treated as never made. Only the loading flag
   * the attempt set is undone; the error, holder and ownership stay as they were, and the result
   * carries the mark so a page two layers up can present nothing. If the server did apply it and
   * only the answer was lost, the lock is this tab session's, and acquiring again takes it over
   * as the same session (rule 9).
   */
  private unansweredAcquire(tracked: TrackedLock): LockResult {
    tracked.state.loading = false;
    this.emit(tracked);
    return { success: false, unanswered: true };
  }

  /**
   * Release a lock. Should be called when done editing.
   */
  async releaseLock(
    resourceType: string,
    resourceId: string
  ): Promise<boolean> {
    const key = this.lockKey(resourceType, resourceId);
    const tracked = this.trackedLocks.get(key);

    if (!tracked) {
      return true; // Nothing to release
    }

    // Clear refresh timer (we no longer own the lock)
    this.clearRefreshTimer(tracked);
    // Note: Do NOT unsubscribe from SSE here - other listeners may still need lock events
    // The SSE subscription is managed by subscribe() and its cleanup function

    // ANNOUNCE THE RELEASE AS OUR OWN BEFORE ASKING FOR IT (@LOCK-025). The server announces the
    // release on the event stream, a separate connection, and that announcement can arrive before
    // this request's own answer does. Without the expectation armed first it reads as a release
    // from somewhere else, and every page shows "released from another tab or session" over the
    // person's own Save or Unlock. Armed with the same bounded expectation a server job's release
    // uses, so the announcement, whichever side of the answer it lands, is reported as this tab's.
    tracked.expectServerReleaseUntil = Date.now() + EXPECT_SERVER_RELEASE_MS;

    try {
      const response = await this.api.release({
        resourceType,
        resourceId,
      });

      if (isUnanswered(response)) {
        // No answer (D-144): treated as never made. The tab still holds the lock as far as it
        // knows, so the heartbeat stopped above is started again; without it the page would show
        // the person as owner while nothing renewed the lease (U21 re-review R1).
        tracked.expectServerReleaseUntil = undefined;
        this.scheduleRefresh(tracked);
        return false;
      }

      // Update state regardless of response. This is the tab's OWN release, so it is reported as
      // such: consumers must not read the person's own Save or Unlock as a loss (@LOCK-004). When
      // the announcement already arrived and was reported, this second report finds the tab
      // already not holding the lock, so no consumer sees a transition in it and it changes nothing.
      // The expectation is disarmed either way: the answer is in, and an announcement still to come
      // for this release is then a plain release of a lock the tab no longer holds.
      tracked.state.isOwner = false;
      tracked.state.holder = undefined;
      tracked.state.fencingToken = undefined;
      tracked.state.leaseExpiresAt = undefined;
      tracked.state.error = undefined;
      tracked.state.loading = false;
      tracked.expectServerReleaseUntil = undefined;
      this.emitOwnRelease(tracked);

      // Clean up tracked lock if no listeners
      if (tracked.listeners.size === 0) {
        tracked.sseUnsubscribe?.();
        this.trackedLocks.delete(key);
      }

      return response.success && response.data?.success === true;
    } catch {
      // The request never completed, so the tab still holds the lock as far as it knows. Disarm, or
      // a real release from somewhere else in the next thirty seconds would be swallowed as ours.
      tracked.expectServerReleaseUntil = undefined;
      return false;
    }
  }

  /**
   * Get current lock state for a resource (local state, not server query)
   */
  getLockState(
    resourceType: string,
    resourceId: string
  ): ResourceLockState | undefined {
    const key = this.lockKey(resourceType, resourceId);
    return this.trackedLocks.get(key)?.state;
  }

  /**
   * Check server for current lock status
   */
  async fetchLockStatus(
    resourceType: string,
    resourceId: string
  ): Promise<ResourceLockDto | null> {
    const response = await this.api.getStatus(resourceType, resourceId);
    return response.success ? (response.data ?? null) : null;
  }

  /**
   * Subscribe to lock state changes for a resource.
   * Optionally fetches initial lock status from server (default: true).
   */
  subscribe(
    resourceType: string,
    resourceId: string,
    listener: ResourceLockListener,
    options?: { fetchInitial?: boolean }
  ): () => void {
    const key = this.lockKey(resourceType, resourceId);
    let tracked = this.trackedLocks.get(key);
    const isNew = !tracked;

    if (!tracked) {
      tracked = this.createTrackedLock(resourceType, resourceId);
      this.trackedLocks.set(key, tracked);
    }

    tracked.listeners.add(listener);

    // Immediately emit current state
    listener(tracked.state);

    // If we're not already subscribed to SSE, do so for monitoring
    if (!tracked.sseUnsubscribe) {
      this.subscribeToLockEvents(tracked);
    }

    // Fetch initial lock status from server if this is a new subscription
    // and we don't already have state (e.g., from a prior acquire)
    const shouldFetch =
      options?.fetchInitial !== false && isNew && !tracked.state.holder;
    if (shouldFetch) {
      // Fire and forget - will update state asynchronously
      this.fetchAndUpdateLockState(tracked).catch(() => void 0);
    }

    return () => {
      tracked.listeners.delete(listener);
      // Clean up if no more listeners and we don't own the lock
      if (tracked.listeners.size === 0 && !tracked.state.isOwner) {
        tracked.sseUnsubscribe?.();
        this.trackedLocks.delete(key);
      }
    };
  }

  /**
   * Fetch lock status from server and update local state.
   * Used to get initial state when subscribing.
   */
  private async fetchAndUpdateLockState(tracked: TrackedLock): Promise<void> {
    const lockDto = await this.fetchLockStatus(
      tracked.resourceType,
      tracked.resourceId
    );

    const now = Date.now();

    if (lockDto && lockDto.leaseExpiresAt) {
      // Check if lock is still valid (not expired)
      const expiresAt = new Date(lockDto.leaseExpiresAt).getTime();
      if (expiresAt > now) {
        // Lock exists and is valid
        const isOurSession = lockDto.ownerSessionId === this.sessionId;
        tracked.state.holder = {
          userId: lockDto.ownerUserId,
          sessionId: lockDto.ownerSessionId,
          leaseExpiresAt: lockDto.leaseExpiresAt,
          acquiredAt: lockDto.acquiredAt ?? new Date().toISOString(),
        };
        tracked.state.isOwner = isOurSession;
        tracked.state.fencingToken = lockDto.fencingToken;
        tracked.state.leaseExpiresAt = new Date(lockDto.leaseExpiresAt);
        tracked.fencingToken = lockDto.fencingToken;
        tracked.leaseExpiresAt = new Date(lockDto.leaseExpiresAt);
        this.emit(tracked);
      }
    }
  }

  /**
   * Get the current fencing token for a resource (for stale write prevention)
   */
  getFencingToken(
    resourceType: string,
    resourceId: string
  ): number | undefined {
    const key = this.lockKey(resourceType, resourceId);
    return this.trackedLocks.get(key)?.fencingToken;
  }

  // ─────────────────────────────────────────────────────────────────────────────
  // Internal Helpers
  // ─────────────────────────────────────────────────────────────────────────────

  private createTrackedLock(
    resourceType: string,
    resourceId: string
  ): TrackedLock {
    return {
      resourceType,
      resourceId,
      fencingToken: 0,
      leaseExpiresAt: new Date(0),
      listeners: new Set(),
      state: {
        resourceType,
        resourceId,
        isOwner: false,
        heldByThisSession: false,
        releasedByThisTab: false,
        takenOver: false,
        loading: false,
      },
    };
  }

  /**
   * Announce that a server job this tab is about to call will release this lock as part of what it
   * does (approve, reject, submit, cancel or complete a review). The release event that follows is
   * then reported with `releasedByThisTab`, so no consumer reads the tab's own decision as a loss.
   * Call it immediately before the request; a refused request should call `cancelExpectedRelease`.
   *
   * WHAT THIS CANNOT COVER: a tab with no stream. The expectation lapses at its bound and the
   * next refresh finds no lock, so the store reports a plain loss after a release that was this
   * tab's own. That is true of every server-side release when the tab has no stream - approve,
   * submit and supersede alike - and it is the correct reading of what the tab can know: it asked
   * for a release and never heard that it happened.
   */
  expectServerRelease(resourceType: string, resourceId: string): void {
    const key = this.lockKey(resourceType, resourceId);
    let tracked = this.trackedLocks.get(key);
    if (!tracked) {
      tracked = this.createTrackedLock(resourceType, resourceId);
      this.trackedLocks.set(key, tracked);
    }
    tracked.expectServerReleaseUntil = Date.now() + EXPECT_SERVER_RELEASE_MS;
  }

  /** The announced server job was refused, so no release is coming. */
  cancelExpectedRelease(resourceType: string, resourceId: string): void {
    const tracked = this.trackedLocks.get(
      this.lockKey(resourceType, resourceId)
    );
    if (tracked) tracked.expectServerReleaseUntil = undefined;
  }

  /** Whether an announced server release is still expected for this lock. */
  private consumeExpectedRelease(tracked: TrackedLock): boolean {
    const until = tracked.expectServerReleaseUntil;
    tracked.expectServerReleaseUntil = undefined;
    return until !== undefined && Date.now() <= until;
  }

  /**
   * Emit once with `releasedByThisTab` set, then clear it: the flag describes one event, not a
   * state, so a later emit for the same lock must not carry it.
   */
  private emitOwnRelease(tracked: TrackedLock): void {
    tracked.state.releasedByThisTab = true;
    this.emit(tracked);
    tracked.state.releasedByThisTab = false;
  }

  private emit(tracked: TrackedLock): void {
    // Derived at emit time rather than maintained by every branch that sets `holder`, so no
    // path can leave it stale against the holder it describes.
    tracked.state.heldByThisSession =
      tracked.state.holder?.sessionId === this.sessionId;
    const stateCopy = { ...tracked.state };
    tracked.listeners.forEach((l) => {
      try {
        l(stateCopy);
      } catch (err) {
        console.warn('[lock-store] Listener error:', err);
      }
    });
  }

  private scheduleRefresh(tracked: TrackedLock): void {
    this.clearRefreshTimer(tracked);

    const now = Date.now();
    const expiresAt = tracked.leaseExpiresAt.getTime();
    const refreshAt = expiresAt - REFRESH_BUFFER_MS;
    let delay = refreshAt - now;

    // Ensure minimum interval
    if (delay < MIN_REFRESH_INTERVAL_MS) {
      delay = MIN_REFRESH_INTERVAL_MS;
    }

    tracked.refreshTimer = setTimeout(() => this.refreshLock(tracked), delay);
  }

  private clearRefreshTimer(tracked: TrackedLock): void {
    if (tracked.refreshTimer) {
      clearTimeout(tracked.refreshTimer);
      tracked.refreshTimer = undefined;
    }
  }

  private async refreshLock(tracked: TrackedLock): Promise<void> {
    if (!tracked.state.isOwner) return;

    try {
      const response = await this.api.refresh({
        resourceType: tracked.resourceType,
        resourceId: tracked.resourceId,
        leaseDurationSeconds: DEFAULT_LEASE_SECONDS,
      });

      if (response.success && response.data?.success) {
        tracked.leaseExpiresAt = new Date(response.data.leaseExpiresAt!);
        tracked.state.leaseExpiresAt = tracked.leaseExpiresAt;
        this.emit(tracked);
        this.scheduleRefresh(tracked);
      } else if (isUnanswered(response)) {
        // No answer is not a lost lock (D-144): nothing is known to have changed, so the tab keeps
        // the lock and tries again on the next beat. Before U21 a status 0 fell into the lost-lock
        // branch below and told the page its lock was gone.
        this.scheduleRefresh(tracked);
      } else {
        // Lost the lock. The holder must reflect who has it NOW, not the "You" recorded when this
        // tab acquired it: leaving our own session as holder made heldByThisSession read true and
        // every consumer still believe the lock was ours, so no banner and a page still claiming a
        // lock the server had given away. A refusal that names another holder is a takeover; one
        // that names nobody (lease lapsed, row gone) is a plain loss.
        const other = response.data?.currentHolder;
        const takenBySomeoneElse =
          !!other && other.sessionId !== this.sessionId;
        tracked.state.isOwner = false;
        tracked.state.holder = takenBySomeoneElse ? other : undefined;
        tracked.state.takenOver = takenBySomeoneElse;
        tracked.state.error =
          response.data?.errorMessage ?? 'Lock refresh failed';
        this.emit(tracked);
      }
    } catch (err) {
      console.warn('[lock-store] Refresh error:', err);
      // Continue trying to refresh
      this.scheduleRefresh(tracked);
    }
  }

  private subscribeToLockEvents(tracked: TrackedLock): void {
    if (tracked.sseUnsubscribe) return;

    try {
      const hub = getSseHub();
      tracked.sseUnsubscribe = hub.subscribeLockResource<LockEventPayload>(
        tracked.resourceType,
        tracked.resourceId,
        (evt) => this.handleLockEvent(tracked, evt)
      );
    } catch {
      // SSE hub not initialized yet, skip
    }
  }

  private handleLockEvent(
    tracked: TrackedLock,
    evt: HubEvent<LockEventPayload>
  ): void {
    const data = evt.data;
    const isOurSession = data.ownerSessionId === this.sessionId;
    // Whether THIS TAB held the lock before this event, by either route: acquired here (`isOwner`)
    // or taken for it by a server job (the review flows), which leaves `isOwner` false while the
    // holder is our session. Loss is judged against this, not `isOwner` alone: a review lock taken
    // over or released from elsewhere used to leave the tab still reading itself as the holder.
    const wasOurs =
      tracked.state.isOwner ||
      tracked.state.holder?.sessionId === this.sessionId;

    switch (data.eventName) {
      case 'lock-acquired':
        if (isOurSession) {
          // An expectation armed for the NEXT release is left alone here. The one-click flows
          // (supersede) acquire, then arm, then call the server; the acquire's own lock-acquired
          // travels over the SSE stream, a separate connection, and can land AFTER the arming.
          // Clearing the expectation on it made the server's release for the successful supersede
          // read as a plain loss and raised the banner over a click that worked. A stale
          // expectation is retired by its thirty-second bound, which is what the bound is for.
          // Acquired FOR this session by a server-side flow the store did not run - the
          // review jobs take the lock with the page's SSE session id. Record the holder as
          // us and the fresh lease, so a holder cached from before (an earlier session of
          // the same person, taken over just now) stops reading as someone else. `isOwner`
          // is left as it is: it means "this store acquired and refreshes it", and it did
          // not. Caught by the AS 2047 lifecycle journey: after Take Over Review the page
          // kept offering Take Over Review, forever.
          tracked.state.holder = {
            userId: data.ownerUserId,
            sessionId: data.ownerSessionId,
            leaseExpiresAt: data.leaseExpiresAt,
            acquiredAt: new Date().toISOString(),
          };
          tracked.state.takenOver = false;
          tracked.state.fencingToken = data.fencingToken;
          tracked.state.leaseExpiresAt = new Date(data.leaseExpiresAt);
          tracked.fencingToken = data.fencingToken;
          tracked.leaseExpiresAt = new Date(data.leaseExpiresAt);
          this.emit(tracked);
        } else if (wasOurs) {
          // Someone else took our lock - whichever way we held it.
          tracked.state.isOwner = false;
          tracked.state.takenOver = true;
          tracked.state.holder = {
            userId: data.ownerUserId,
            sessionId: data.ownerSessionId,
            leaseExpiresAt: data.leaseExpiresAt,
            acquiredAt: new Date().toISOString(),
          };
          this.clearRefreshTimer(tracked);
          this.emit(tracked);
        } else if (!isOurSession) {
          // Lock is now held by someone else
          tracked.state.holder = {
            userId: data.ownerUserId,
            sessionId: data.ownerSessionId,
            leaseExpiresAt: data.leaseExpiresAt,
            acquiredAt: new Date().toISOString(),
          };
          this.emit(tracked);
        }
        break;

      case 'lock-released':
      case 'lock-expired':
        if (!isOurSession) {
          // Someone else released or their lock expired. If this tab still read itself as owner
          // under a session id it no longer has (the session self-healed and was re-issued), the
          // released lock was ours by that old id and the ownership goes with it.
          const wasOursByThatSession =
            tracked.state.holder?.sessionId === data.ownerSessionId &&
            tracked.state.isOwner;
          if (wasOursByThatSession) tracked.state.isOwner = false;
          tracked.state.holder = undefined;
          tracked.state.takenOver = false;
          this.emit(tracked);
        } else if (this.consumeExpectedRelease(tracked)) {
          // A server job this tab asked for released the lock as part of the decision: the tab's
          // own doing, reported as such so it is not shown as a loss.
          tracked.state.isOwner = false;
          tracked.state.holder = undefined;
          tracked.state.takenOver = false;
          this.clearRefreshTimer(tracked);
          this.emitOwnRelease(tracked);
        } else {
          // Our own session's lock was released or expired. Expected when the session ended in
          // another tab (sign-out, switch account) and the server released everything it held, or
          // when a review lock's fifteen-minute lease lapsed; this tab has lost the lock and must
          // read as not holding it whether or not this store acquired it. Not a takeover: nobody
          // holds it now, so `takenOver` stays false and consumers show a plain loss.
          tracked.state.isOwner = false;
          tracked.state.holder = undefined;
          tracked.state.takenOver = false;
          this.clearRefreshTimer(tracked);
          this.emit(tracked);
        }
        break;

      case 'lock-refreshed':
        // Update lease expiry if it's not our lock
        if (!isOurSession && tracked.state.holder) {
          tracked.state.holder.leaseExpiresAt = data.leaseExpiresAt;
          this.emit(tracked);
        }
        break;
    }
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// Singleton Export
// ─────────────────────────────────────────────────────────────────────────────

let _instance: LockStore | undefined;

export function getLockStore(): LockStore {
  if (!_instance) {
    _instance = new LockStore();
  }
  return _instance;
}

/** Alias for getLockStore() */
export const lockStore = {
  get instance(): LockStore {
    return getLockStore();
  },
};
